import React, { useState, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { Image, UploadCloud, Trash2, RefreshCw, AlertCircle, Crop } from 'lucide-react'
import styled, { css } from 'styled-components'
import Text from 'components/_UI/Text'
import ImageCropperModal from 'components/_UI/ImageCropperModal'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'

const BANNER_ASPECT = 16 / 9

export default function ImageInfo({ onToggleImage, onDeleteFile, errorImage, allowedFiles, setErrorImage, setErrorTypeImage, errorTypeImage, isEdit }) {
    const [preview, setPreview] = useState(null)
    const [fileName, setFileName] = useState(null)
    const [sourceFile, setSourceFile] = useState(null)
    const [isCropperOpen, setIsCropperOpen] = useState(false)

    useEffect(() => {
        if (isEdit?.images || isEdit?.thumb) {
            setPreview(isEdit?.thumb || null)
        }
    }, [isEdit])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'image/*': ['.jpg', '.jpeg', '.png'] },
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setErrorImage(false)
                setErrorTypeImage(false)
                setSourceFile(acceptedFiles[0])
                setIsCropperOpen(true)
            }
        },
    })

    function handleCropConfirm(croppedFile) {
        if (preview) {
            URL.revokeObjectURL(preview)
        }
        setPreview(URL.createObjectURL(croppedFile))
        setFileName(croppedFile.name)
        setIsCropperOpen(false)
        onToggleImage(croppedFile)
    }

    function handleCropCancel() {
        setIsCropperOpen(false)
        // Sem imagem confirmada ainda: descarta o arquivo escolhido.
        if (!preview) {
            setSourceFile(null)
        }
    }

    function handleDelete() {
        if (preview) {
            URL.revokeObjectURL(preview)
        }
        setPreview(null)
        setFileName(null)
        setSourceFile(null)
        onDeleteFile()
    }

    function handleRetry() {
        handleDelete()
        setErrorImage(false)
        setErrorTypeImage(false)
    }

    const hasError = errorImage || errorTypeImage

    return (
        <Container>
            <SectionHeader>
                <SectionIcon><Image size={18} color={colors.occasionalPurple} /></SectionIcon>
                <div>
                    <Text type='bold' color={colors.nightDark}>
                        Imagem de capa
                    </Text>
                    <Text name='small' color={colors.night}>
                        Essa imagem será exibida como capa do seu evento.
                    </Text>
                </div>
            </SectionHeader>

            {!preview ? (
                <Dropzone {...getRootProps()} isDragActive={isDragActive} hasError={hasError}>
                    <input {...getInputProps()} />

                    <DropzoneContent>
                        <IconCircle isDragActive={isDragActive} hasError={hasError}>
                            {hasError
                                ? <AlertCircle size={24} color={colors.danger} />
                                : <UploadCloud size={24} color={isDragActive ? colors.occasionalPurple : colors.night} />
                            }
                        </IconCircle>

                        {hasError ? (
                            <>
                                <DropzoneTitle style={{ color: colors.danger }}>
                                    {errorTypeImage || 'Erro ao enviar a imagem'}
                                </DropzoneTitle>
                                <RetryLink type="button" onClick={(e) => { e.stopPropagation(); handleRetry(); }}>
                                    <RefreshCw size={13} />
                                    Tentar novamente
                                </RetryLink>
                            </>
                        ) : (
                            <>
                                <DropzoneTitle>
                                    {isDragActive
                                        ? 'Solte a imagem aqui'
                                        : 'Arraste uma imagem ou clique para selecionar'
                                    }
                                </DropzoneTitle>
                                <DropzoneSpecs>
                                    {allowedFiles} &middot; Máximo 2 MB &middot; Recorte 16:9
                                </DropzoneSpecs>
                            </>
                        )}
                    </DropzoneContent>
                </Dropzone>
            ) : (
                <PreviewContainer>
                    <PreviewImage src={preview} alt="Pré-visualização da imagem de capa" />
                    <PreviewOverlay>
                        {sourceFile && (
                            <OverlayButton type="button" onClick={() => setIsCropperOpen(true)}>
                                <Crop size={18} />
                                Ajustar
                            </OverlayButton>
                        )}
                        <OverlayButton type="button" isDanger onClick={handleDelete}>
                            <Trash2 size={18} />
                            Remover
                        </OverlayButton>
                    </PreviewOverlay>
                    {fileName && (
                        <PreviewFileName>{fileName}</PreviewFileName>
                    )}
                </PreviewContainer>
            )}

            <ImageCropperModal
                isOpen={isCropperOpen}
                file={sourceFile}
                aspect={BANNER_ASPECT}
                title="Ajustar imagem de capa"
                description="Arraste para reposicionar e use o zoom. A capa será exibida em 16:9."
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
            />
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;

    & > div:last-child > p:first-child {
        margin-bottom: 2px;
    }
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
    margin-top: 2px;
`;

const Dropzone = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 180px;
    border: 2px dashed ${colors.nightLight};
    border-radius: 12px;
    background: ${colors.neutralBackground};
    cursor: pointer;
    transition: all .25s ease;
    padding: 28px 20px;

    &:hover {
        border-color: ${colors.occasionalPurple};
        background: #F9F5FF;
    }

    ${({ isDragActive }) => isDragActive && css`
        border-color: ${colors.occasionalPurple};
        background: #F3EAFF;
        transform: scale(1.01);
    `}

    ${({ hasError }) => hasError && css`
        border-color: ${colors.dangerLight};
        background: #FEF2F2;

        &:hover {
            border-color: ${colors.danger};
            background: #FEF2F2;
        }
    `}

    @media screen and (min-width: ${breakpoints.md}) {
        min-height: 200px;
    }
`;

const DropzoneContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    transition: all .25s ease;

    ${({ isDragActive }) => isDragActive && css`
        background: #F3EAFF;
        border-color: ${colors.occasionalPurple}40;
    `}

    ${({ hasError }) => hasError && css`
        background: #FEE2E2;
        border-color: ${colors.dangerLight};
    `}
`;

const DropzoneTitle = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.4;
`;

const DropzoneSpecs = styled.p`
    font-size: 12px;
    color: ${colors.night};
    margin: 0;
`;

const RetryLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    color: ${colors.primary};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    &:hover {
        text-decoration: underline;
    }
`;

const PreviewContainer = styled.div`
    position: relative;
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${colors.neutralDark};
`;

const PreviewImage = styled.img`
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 240px;
    }
`;

const PreviewOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity .25s ease;

    ${PreviewContainer}:hover &,
    &:focus-within {
        opacity: 1;
    }
`;

const OverlayButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 8px;
    border: none;
    background: ${colors.neutralLight};
    color: ${({ isDanger }) => (isDanger ? colors.danger : colors.nightDark)};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;

    &:hover {
        background: ${({ isDanger }) => (isDanger ? '#FEE2E2' : colors.neutralDark)};
    }

    &:focus-visible {
        outline: 2px solid ${colors.neutralLight};
        outline-offset: 2px;
    }
`;

const PreviewFileName = styled.div`
    padding: 8px 14px;
    background: ${colors.neutralBackground};
    font-size: 12px;
    color: ${colors.night};
    border-top: 1px solid ${colors.neutralDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
